import { useCallback, useRef } from 'react';
import { API_CONFIG, createAuthenticatedWebSocket } from '../../../utils/constants';
import { PCM_WORKLET_SOURCE } from '../audio/pcmWorkletSource';

export const OPENAI_STT_SAMPLE_RATE = 24000;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export type OpenAiSttHandlers = {
  onMessage?: (data: Record<string, unknown>) => void;
  onError?: (message: string) => void;
};

export function useOpenAiRealtimeStt(handlers: OpenAiSttHandlers = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(handlers);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const captureActiveRef = useRef(false);
  const connectPromiseRef = useRef<Promise<void> | null>(null);

  handlersRef.current = handlers;

  const sendJson = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  const disconnect = useCallback(() => {
    captureActiveRef.current = false;
    workletNodeRef.current?.disconnect();
    sourceNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    sourceNodeRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      void audioContextRef.current.close();
    }
    audioContextRef.current = null;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    connectPromiseRef.current = null;
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    connectPromiseRef.current = (async () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const ws = await createAuthenticatedWebSocket(API_CONFIG.ASR_WEBSOCKET_URL);
      wsRef.current = ws;

      await new Promise<void>((resolve, reject) => {
        const onOpen = () => {
          ws.removeEventListener('error', onError);
          resolve();
        };
        const onError = () => {
          ws.removeEventListener('open', onOpen);
          reject(new Error('ASR WebSocket connection failed'));
        };
        if (ws.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }
        ws.addEventListener('open', onOpen);
        ws.addEventListener('error', onError);
      });

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as Record<string, unknown>;
          handlersRef.current.onMessage?.(data);
        } catch {
          // ignore malformed payloads
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
      };

      ws.onerror = () => {
        handlersRef.current.onError?.('ASR WebSocket error');
      };
    })();

    try {
      await connectPromiseRef.current;
    } finally {
      connectPromiseRef.current = null;
    }
  }, []);

  const startSttSession = useCallback(async () => {
    await connect();
    sendJson({ type: 'stt_session_start' });
  }, [connect, sendJson]);

  const startMicCapture = useCallback(async (stream: MediaStream) => {
    await connect();
    await startSttSession();

    captureActiveRef.current = true;

    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new AudioContext({ sampleRate: OPENAI_STT_SAMPLE_RATE });
    }
    const audioContext = audioContextRef.current;
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const blob = new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' });
    const workletUrl = URL.createObjectURL(blob);
    await audioContext.audioWorklet.addModule(workletUrl);
    URL.revokeObjectURL(workletUrl);

    sourceNodeRef.current?.disconnect();
    workletNodeRef.current?.disconnect();

    const sourceNode = audioContext.createMediaStreamSource(stream);
    const workletNode = new AudioWorkletNode(audioContext, 'pcm-worklet-processor');
    workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (!captureActiveRef.current) {
        return;
      }
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      sendJson({
        type: 'pcm_append',
        audio_data: arrayBufferToBase64(event.data),
      });
    };

    sourceNode.connect(workletNode);
    sourceNodeRef.current = sourceNode;
    workletNodeRef.current = workletNode;
  }, [connect, sendJson, startSttSession]);

  const stopMicCapture = useCallback(() => {
    captureActiveRef.current = false;
    workletNodeRef.current?.disconnect();
    sourceNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    sourceNodeRef.current = null;
  }, []);

  const commitTurn = useCallback(
    async (questionNumber: number) => {
      stopMicCapture();
      await connect();
      sendJson({
        type: 'stt_commit',
        question_number: questionNumber,
      });
    },
    [connect, sendJson, stopMicCapture],
  );

  return {
    connect,
    disconnect,
    startSttSession,
    startMicCapture,
    stopMicCapture,
    commitTurn,
    sendJson,
  };
}
